import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Plus, Trash2, Search, Pencil, Upload } from "lucide-react";
import { handleGet, handleRemove } from "../../../controllers/controller.all";
import Pagination from "../../../components/ui/Pagination";
import SortableHeader from "../../../components/ui/SortableHeader";

export default function StudentList() {
    const user = useSelector((state) => state.userReducer?.user);
    const token = user?.access_token || "";
    const ecoleId = user?.ecole?._id || user?.ecole;
    
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    
    // Pagination & Sorting state
    const [currentPage, setCurrentPage] = useState(1);
    const [sortKey, setSortKey] = useState("nom");
    const [sortOrder, setSortOrder] = useState("asc");
    const itemsPerPage = 20;

    const fetchStudents = async () => {
        setLoading(true);
        if (ecoleId) {
            await handleGet(`/user/s/all/eleves/${ecoleId}?skip=0&limit=100000`, (res) => {
                if (res?.users) {
                    setStudents(res.users);
                }
            }, token);
        }
        setLoading(false);
    };

    useEffect(() => {
        if(token) fetchStudents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ecoleId, token]);
    
    const handleSort = (key, order) => {
        setSortKey(key);
        setSortOrder(order);
    };

    const handleDelete = async (id) => {
        await handleRemove(`/user/${id}`, fetchStudents, token);
    };

    const filteredStudents = students.filter(s => !search || 
        s.prenom?.toLowerCase().includes(search.toLowerCase()) || 
        s.nom?.toLowerCase().includes(search.toLowerCase()) || 
        s.numero_etudiant?.toLowerCase().includes(search.toLowerCase())
    );

    const sortedStudents = [...filteredStudents].sort((a, b) => {
        let valA = a[sortKey] || "";
        let valB = b[sortKey] || "";
        
        if (valA < valB) return sortOrder === "asc" ? -1 : 1;
        if (valA > valB) return sortOrder === "asc" ? 1 : -1;
        return 0;
    });

    const paginatedStudents = sortedStudents.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Étudiants</h1>
                    <p className="text-sm text-gray-500 mt-1">Gérez la liste de tous vos étudiants inscrits.</p>
                </div>
                <div className="flex items-center gap-3">
                    <Link 
                        to="/students/batch" 
                        className="inline-flex items-center gap-2 bg-white border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 rounded-lg transition"
                    >
                        <Upload className="h-4 w-4" />
                        <span className="hidden sm:inline">Import en masse</span>
                    </Link>
                    <Link 
                        to="/students/create" 
                        className="inline-flex items-center gap-2 bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 rounded-lg transition"
                    >
                        <Plus className="h-4 w-4" />
                        <span className="hidden sm:inline">Nouvel Étudiant</span>
                    </Link>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <div className="relative max-w-sm w-full text-gray-400">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" />
                        <input 
                            type="text" 
                            placeholder="Rechercher..." 
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <SortableHeader label="Nom & Prénom" sortKey="nom" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                <SortableHeader label="Contact" sortKey="tel" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                <SortableHeader label="Matricule" sortKey="numero_etudiant" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-10 text-center text-sm text-gray-500">Chargement...</td>
                                </tr>
                            ) : paginatedStudents.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-10 text-center text-sm text-gray-500">Aucun étudiant trouvé.</td>
                                </tr>
                            ) : (
                                paginatedStudents.map((student) => (
                                    <tr key={student._id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">{student.prenom} {student.nom}</div>
                                            <div className="text-sm text-gray-500">{student.classe?.libelle || "Aucune classe"}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{student.tel}</div>
                                            <div className="text-sm text-gray-500 block truncate max-w-xs">{student.email}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                {student.numero_etudiant || "N/A"}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <Link 
                                                to={`/students/update/${student._id}`}
                                                className="text-indigo-600 hover:text-indigo-800 bg-indigo-50 p-2 rounded-lg transition inline-block mr-2"
                                                title="Modifier"
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </Link>
                                            <button 
                                                onClick={() => handleDelete(student._id)}
                                                className="text-red-500 hover:text-red-700 bg-red-50 p-2 rounded-lg transition"
                                                title="Supprimer"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                
                {filteredStudents.length > itemsPerPage && (
                    <Pagination 
                        currentPage={currentPage} 
                        totalItems={filteredStudents.length} 
                        itemsPerPage={itemsPerPage} 
                        onPageChange={setCurrentPage} 
                    />
                )}
            </div>
        </div>
    );
}
